import { H1, UserCombobox } from '@/components'
import { MonthYearPicker } from '@/components/month-year-picker'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO, startOfMonth } from 'date-fns'
import { ArrowLeftIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { KEEP_SCROLL_STATE } from '@/utils/hooks/use-scroll-restoration'
import { DriverGradeDayInspector } from './driver-grade-day-inspector'
import { DriverGradeDaysChart } from './driver-grade-days-chart'
import { DriverGradeMonthTable } from './driver-grade-month-table'
import { GradePill, InfoLabel, PerfectScoreCard, ScoreBar } from './grade-parts'
import { formatNumber } from './metrics'
import { format as formatMonth } from '@/utils/date-fns/format'
import { DRIVER_GRADE_DAYS_QUERY, DRIVER_GRADE_MONTH_QUERY } from './queries'
import { useMetricTexts } from './use-metric-texts'

const MONTH_FORMAT = 'yyyy-MM-dd'

const useDashboardParams = () => {
  const [params, setParams] = useSearchParams()
  const monthParam = params.get('month')
  const month = useMemo(
    () => startOfMonth(monthParam ? parseISO(monthParam) : new Date()),
    [monthParam]
  )

  const update = (
    changes: Record<string, string | null>,
    { keepScroll = false } = {}
  ) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes))
      if (value) next.set(key, value)
      else next.delete(key)
    setParams(next, keepScroll ? { state: KEEP_SCROLL_STATE } : undefined)
  }

  return {
    month,
    monthKey: format(month, MONTH_FORMAT),
    user: params.get('user') ?? '',
    day: params.get('day'),
    update,
  }
}

const DriverMonth: React.FC<{
  user: string
  month: Date
  monthKey: string
  day: string | null
  onSelectDay(day: string): void
}> = ({ user, month, monthKey, day, onSelectDay }) => {
  const translations = useTranslate({
    grade: 'driver-grade.grade',
    gradeInfo: 'driver-grade.info-month',
    days: 'driver-grade.graded-days',
    distance: 'common.distance',
    dayByDay: 'driver-grade.day-by-day',
    selectDay: 'driver-grade.select-day-hint',
    noGrades: 'driver-grade.no-grades',
  })
  const { label, info } = useMetricTexts()

  const { data: monthData } = useQuery(DRIVER_GRADE_MONTH_QUERY, {
    variables: { month: monthKey },
  })
  const { data: daysData } = useQuery(DRIVER_GRADE_DAYS_QUERY, {
    variables: { user, month: monthKey },
  })

  const row = monthData?.driverGradeMonth.rows.find(r => r.user.id === user)
  const maximumGrade = monthData?.driverGradeMonth.maximumGrade ?? 100
  const days = daysData?.driverGradeDays ?? []

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          <div className="border-b border-stone-200 px-5 pt-4 pb-3 text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
            {formatMonth(month, 'MMMM yyyy')}
          </div>
          <div className="grid grid-cols-3 divide-x divide-stone-200">
            <div className="space-y-1 px-5 py-4">
              <InfoLabel
                label={translations.grade}
                info={translations.gradeInfo}
              />
              <div>
                <GradePill grade={row?.grade} />
              </div>
            </div>
            <div className="space-y-1 px-5 py-4">
              <div className="text-xs font-semibold uppercase text-stone-500">
                {translations.days}
              </div>
              <div className="text-sm font-medium tabular-nums text-stone-900">
                {row?.days ?? 0}
              </div>
            </div>
            <div className="space-y-1 px-5 py-4">
              <div className="text-xs font-semibold uppercase text-stone-500">
                {translations.distance}
              </div>
              <div className="text-sm font-medium tabular-nums text-stone-900">
                {formatNumber(row?.distanceKm ?? 0, 0)} km
              </div>
            </div>
          </div>
          {row && (
            <div className="space-y-3 border-t border-stone-200 px-5 py-4">
              {row.metrics.map(metric => (
                <div key={metric.key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <InfoLabel
                      label={label(metric.key)}
                      info={info(metric, row.metrics)}
                    />
                    <span className="text-sm font-semibold tabular-nums">
                      {formatNumber(metric.value)} %
                    </span>
                  </div>
                  <ScoreBar score={metric.score} />
                  <div className="text-xs tabular-nums text-stone-500">
                    {formatNumber(metric.score)} / 100
                  </div>
                </div>
              ))}
            </div>
          )}
          {row && (
            <div className="border-t border-stone-200">
              <PerfectScoreCard metrics={row.metrics} />
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-2xl bg-white px-5 py-4 shadow-card">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xs font-semibold uppercase text-stone-500">
              {translations.dayByDay}
            </div>
            <div className="text-xs text-stone-500">
              {days.length ? translations.selectDay : translations.noGrades}
            </div>
          </div>
          <DriverGradeDaysChart
            key={monthKey}
            month={month}
            days={days}
            maximumGrade={maximumGrade}
            selected={day}
            onSelect={onSelectDay}
          />
        </div>
      </div>

      {day && (
        <DriverGradeDayInspector
          key={`${user}-${day}`}
          user={user}
          date={day}
        />
      )}
    </div>
  )
}

export const DriverGradeDashboard: React.FC = () => {
  const translations = useTranslate({
    title: 'driver-grade.title',
    beta: 'driver-grade.beta',
    intro: 'driver-grade.intro',
    month: 'common.month',
    driver: 'common.driver',
    allDrivers: 'driver-grade.all-drivers',
    version: ['driver-grade.score-version', { version: '' }],
  })
  const { month, monthKey, user, day, update } = useDashboardParams()

  const { data, loading } = useQuery(DRIVER_GRADE_MONTH_QUERY, {
    variables: { month: monthKey },
  })
  const rows = useMemo(() => data?.driverGradeMonth.rows ?? [], [data])

  return (
    <div className="relative box-border space-y-4 px-4 py-4 md:px-[52px] md:py-8 md:pb-[52px]">
      <div className="flex flex-wrap items-center gap-3">
        <H1 className="!mb-0">{translations.title}</H1>
        <span className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-900">
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {translations.beta}
        </span>
        {data && (
          <span className="text-xs text-stone-400">
            {translations.version({
              version: data.driverGradeMonth.scoreVersion,
            })}
          </span>
        )}
      </div>
      <p className="max-w-3xl text-sm text-stone-600">{translations.intro}</p>

      <div className="grid gap-4 rounded-2xl bg-white px-5 py-4 shadow-card sm:grid-cols-2 lg:max-w-3xl">
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase text-stone-500">
            {translations.month}
          </label>
          <MonthYearPicker
            value={month}
            disableAfter
            className="w-full min-w-0"
            onChange={date =>
              update({
                month: format(startOfMonth(date), MONTH_FORMAT),
                day: null,
              })
            }
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold uppercase text-stone-500">
            {translations.driver}
          </label>
          <UserCombobox
            mode="single"
            value={user}
            className="min-w-0"
            placeholder={translations.allDrivers}
            onChange={value => update({ user: value || null, day: null })}
          />
        </div>
      </div>

      {user ? (
        <>
          <button
            type="button"
            onClick={() => update({ user: null, day: null })}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-stone-900"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {translations.allDrivers}
          </button>
          <DriverMonth
            user={user}
            month={month}
            monthKey={monthKey}
            day={day}
            onSelectDay={selected =>
              update({ day: selected }, { keepScroll: true })
            }
          />
        </>
      ) : (
        <DriverGradeMonthTable rows={rows} loading={loading} />
      )}
    </div>
  )
}
